/**
 * An MSAL app's "give me a token for my API" (#242 follow-on).
 *
 * Uses the ACTIVE account only — the one startMsalApp (@higgins/ui) chose from
 * the portal's login_hint. Falling back to the first cached account would let a
 * shared device call the API as the previous user, so with no active account
 * this signs in again instead.
 *
 * Silent first; if that needs interaction, a full-page redirect (silent iframe
 * renewal is blocked by WebKit cookie rules on iOS/iPadOS, Edge included, and
 * these apps are used on iPads and phones).
 *
 * Never a redirect loop: if this page already sent the user to Entra within
 * the last minute and still has no token, it stops and says so instead of
 * redirecting again. Measured 2026-10-13: a silent failure that persisted
 * after sign-in redirected 15 times in 85 s until Entra refused the app
 * ("We couldn't sign you in"). Why the silent call failed is logged to the
 * console before every redirect — it used to be thrown away.
 *
 * Structural like startMsalApp, so this package doesn't depend on
 * @azure/msal-browser; each app passes its own PublicClientApplication.
 */

export interface MsalTokenClient<A> {
  getActiveAccount(): A | null;
  acquireTokenSilent(request: { scopes: string[]; account: A }): Promise<{ accessToken: string }>;
  acquireTokenRedirect(request: { scopes: string[]; account?: A }): Promise<void>;
  loginRedirect(request: { scopes: string[] }): Promise<void>;
}

/** When this tab last sent the user to Entra for a token; survives the redirect. */
export interface RedirectMemory {
  lastRedirectAt(): number | null;
  remember(at: number | null): void;
  now(): number;
}

/** A redirect back to Entra this soon after the last one, with no token in between, is a loop. */
const REDIRECT_LOOP_WINDOW_MS = 60_000;
const LAST_REDIRECT_KEY = 'higgins.signInRedirectAt';

/** Thrown instead of redirecting again: sign-in came back but the app still can't get a token. */
export class SignInLoopError extends Error {
  /** Why the last silent token request failed (also logged to the console). */
  readonly reason: unknown;

  constructor(reason: unknown) {
    super("Sign-in didn't complete. Reload the page to try again; if it keeps happening, tell IT.");
    this.name = 'SignInLoopError';
    this.reason = reason;
  }
}

/** Per tab (sessionStorage), so another tab's sign-in doesn't count; storage blocked → no memory. */
const sessionRedirectMemory: RedirectMemory = {
  lastRedirectAt() {
    try {
      const at = Number(globalThis.sessionStorage?.getItem(LAST_REDIRECT_KEY));
      return Number.isFinite(at) && at > 0 ? at : null;
    } catch {
      return null;
    }
  },
  remember(at) {
    try {
      if (at === null) globalThis.sessionStorage?.removeItem(LAST_REDIRECT_KEY);
      else globalThis.sessionStorage?.setItem(LAST_REDIRECT_KEY, String(at));
    } catch {
      // Storage blocked: no loop guard, but sign-in still works.
    }
  },
  now: () => Date.now(),
};

/** Never resolves: the page is navigating away to Entra. */
const NAVIGATING_AWAY = new Promise<string>(() => {});

export function createAccessTokenGetter<A>(
  msal: MsalTokenClient<A>,
  scopes: string[],
  memory: RedirectMemory = sessionRedirectMemory,
): () => Promise<string> {
  // Several API calls can fail together; only the first redirects, and the
  // rest share its outcome — including MSAL refusing to start it.
  let redirect: Promise<string> | null = null;

  async function redirectOnce(reason: unknown, go: () => Promise<void>): Promise<string> {
    if (redirect) return redirect;
    const last = memory.lastRedirectAt();
    const now = memory.now();
    if (last !== null && now - last < REDIRECT_LOOP_WINDOW_MS) {
      console.error('[sign-in] still no token after signing in — not redirecting again', reason);
      // Forget it, so the reload the message asks for gets one fresh attempt — a person's choice, never a loop.
      memory.remember(null);
      throw new SignInLoopError(reason);
    }
    console.warn('[sign-in] no token without signing in — redirecting to Microsoft', reason);
    memory.remember(now);
    // Inside the chain, so an MSAL that throws instead of rejecting is handled the same way.
    redirect = Promise.resolve().then(go).then(
      () => NAVIGATING_AWAY,
      (err: unknown) => {
        // MSAL refused to start the redirect: we never left, so it isn't a redirect to remember.
        redirect = null;
        memory.remember(last);
        throw err;
      },
    );
    return redirect;
  }

  return async () => {
    const account = msal.getActiveAccount();
    if (!account) return redirectOnce('no active account', () => msal.loginRedirect({ scopes }));
    try {
      const { accessToken } = await msal.acquireTokenSilent({ scopes, account });
      memory.remember(null);
      return accessToken;
    } catch (err) {
      return redirectOnce(err, () => msal.acquireTokenRedirect({ scopes, account }));
    }
  };
}
